import {
  Box,
  HStack,
  Text,
  TextProps,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import { RiCheckboxCircleFill } from "@remixicon/react";
import { Fragment, useMemo } from "react";

import { Avatar } from "@/common/components/Avatar";
import CompanyDisplay from "@/common/components/CompanyDisplay";
import { FilterItem } from "@/common/components/filters/FilterItem";
import { UserDisplay } from "@/common/components/UserDisplay";
import { formatPercentage } from "@/common/utils/numberFormatting";
import { SegmentDisplay } from "@/company/components/SegmentDisplay";
import { SegmentSizeIndicator } from "@/company/components/SegmentSizeIndicator";
import { useSegments } from "@/company/data/useSegments";
import { Entity } from "@reflag/shared/featureAPI";
import {
  MAX_ROLLOUT_THRESHOLD,
  UIFilter,
  USER_ID_CONTEXT_FIELD,
} from "@reflag/shared/filter";
import { getFraction } from "@reflag/shared/utils/getFraction";
import pluralize from "@reflag/shared/utils/pluralize";
import { SummaryItem } from "@reflag/shared/utils/summaryItem";

function NoAccessSummary() {
  return (
    <HStack color="dimmed" fontSize="sm" spacing={1.5}>
      <Text as="span">No one</Text>
    </HStack>
  );
}

function SomeNoRulesSummary() {
  return (
    <HStack color="dimmed" fontSize="sm" spacing={1.5}>
      <Text as="span">Some, no rules</Text>
    </HStack>
  );
}

function FilterItemRenderer({ filter }: { filter: UIFilter }) {
  if (filter.type !== "group") {
    return <FilterItem filter={filter} noWrap={true} compact showIcon />;
  } else if (filter.filters?.length === 1) {
    return (
      <FilterItem filter={filter.filters[0]} noWrap={true} compact showIcon />
    );
  } else {
    return (
      <Text as={"span"} whiteSpace="nowrap">
        1 rule
      </Text>
    );
  }
}

type AccessSummaryProps = TextProps & {
  targetingSummary?: SummaryItem[] | null;
  wrap?: boolean;
  hideSeparator?: boolean;
};

export function AccessSummary({
  targetingSummary,
  wrap,
  hideSeparator,
  ...rest
}: AccessSummaryProps) {
  const checkmarkColor = useToken(
    "colors",
    useColorModeValue("gray.950", "white"),
  );

  const summary = targetingSummary?.map((item) => {
    switch (item.slotType) {
      case "entity": {
        switch (item.type) {
          case "company": {
            return {
              key: item.entity.id,
              component: (
                <CompanyDisplay
                  avatarSize="2xs"
                  company={item.entity}
                  gap={1.5}
                  isTruncated
                />
              ),
            };
          }
          case "user": {
            return {
              key: item.entity.id,
              component: (
                <UserDisplay gap={1.5} user={item.entity} isTruncated />
              ),
            };
          }
          case "segment": {
            return {
              key: item.entity.id,
              component: (
                <SegmentDisplay
                  gap={1.5}
                  iconSize={3.5}
                  segment={{
                    id: item.entity.id,
                    name: item.entity.name ?? item.entity.id,
                  }}
                  isTruncated
                />
              ),
            };
          }
          case "rule":
            return {
              key: JSON.stringify({
                filter: item.entity.filter,
                partialRolloutContextAttribute:
                  item.entity.partialRolloutContextAttribute,
                partialRolloutThreshold: item.entity.partialRolloutThreshold,
              }),
              component: (
                <HStack {...rest}>
                  {item.entity.partialRolloutThreshold !==
                    MAX_ROLLOUT_THRESHOLD && (
                    <HStack flexShrink={0} spacing={1} whiteSpace="nowrap">
                      <Text as="span">
                        {formatPercentage(
                          getFraction(
                            item.entity.partialRolloutThreshold,
                            MAX_ROLLOUT_THRESHOLD,
                          ),
                        )}
                      </Text>
                      <Text as="span" color="dimmed">
                        of{" "}
                        {item.entity.partialRolloutContextAttribute ===
                        USER_ID_CONTEXT_FIELD
                          ? "users"
                          : "companies"}{" "}
                        matching
                      </Text>
                    </HStack>
                  )}
                  <FilterItemRenderer filter={item.entity.filter} />
                </HStack>
              ),
            };
          default:
            console.error(`Unknown type ${item} in access summary`, item);
            return null;
        }
      }
      case "summary": {
        switch (item.type) {
          case "company":
            return {
              key: item.samples.map((s) => s.id).join(","),
              component: (
                <GroupSummary
                  count={item.count}
                  entityType="company"
                  sampleEntities={item.samples}
                />
              ),
            };
          case "user":
            return {
              key: item.samples.map((s) => s.id).join(","),
              component: (
                <GroupSummary
                  count={item.count}
                  entityType="user"
                  sampleEntities={item.samples}
                />
              ),
            };
          case "segment":
            return {
              key: item.samples.map((s) => s.id).join(","),
              component: (
                <SegmentsSummary
                  count={item.count}
                  sampleEntities={item.samples}
                />
              ),
            };
          case "rule":
            return {
              key: JSON.stringify(item.samples),
              component: (
                <Text as={"span"}>
                  {item.count} {pluralize("rule", item.count)}
                </Text>
              ),
            };
          default:
            console.error(`Unknown type ${item} in access summary`, item);
            return null;
        }
      }
      case "more":
        return {
          key: "more",
          component: (
            <Text as={"span"} isTruncated>
              more...
            </Text>
          ),
        };
      case "none":
        return {
          key: "none",
          component: <NoAccessSummary />,
        };
      case "everyone":
        return {
          key: "everyone",
          component: (
            <HStack spacing={1.5}>
              <RiCheckboxCircleFill color={checkmarkColor} size={16} />
              <Text as="span" fontWeight="medium" {...rest}>
                Everyone
              </Text>
            </HStack>
          ),
        };
      case "optIns":
        return {
          key: "optIns",
          component: (
            <Text as="span" fontWeight="medium" {...rest}>
              Opt-ins
            </Text>
          ),
        };
      case "someNoRules":
        return {
          key: "someNoRules",
          component: <SomeNoRulesSummary />,
        };
    }
  });

  return (
    <HStack
      flexWrap={wrap ? "wrap" : undefined}
      fontSize="sm"
      my={-0.5}
      spacing={1.5}
    >
      {summary?.length ? (
        summary.map((item, i) => (
          <Fragment key={item?.key}>
            {item?.component}
            {!hideSeparator && i < summary.length - 1 ? (
              <Box key={`plus-${i}`} as="span" color="dimmed">
                {" + "}
              </Box>
            ) : null}
          </Fragment>
        ))
      ) : (
        <NoAccessSummary />
      )}
    </HStack>
  );
}

export function SegmentsSummary({
  count,
  sampleEntities,
}: {
  count: number;
  sampleEntities: Entity[];
}) {
  const { data } = useSegments({ includeDeleted: true });

  const sampledSegments = useMemo(() => {
    return sampleEntities
      ?.map((entity) => {
        return data?.find((segment) => segment.id === entity.id);
      })
      .filter((segment) => segment !== undefined);
  }, [data, sampleEntities]);

  return (
    <HStack spacing={0.5}>
      <Box display={"flex"} flexDirection={"row-reverse"} gap={0.5}>
        {sampledSegments?.map((segment, i) => (
          <Box
            key={`segment-${segment?.id}`}
            backgroundColor={"appBackground"}
            borderRadius={"100%"}
            boxSize={5}
            marginInlineEnd={i > 0 ? -2 : 0}
            padding={0.5}
            position={"relative"}
          >
            <SegmentSizeIndicator segmentId={segment?.id} size={4} />
          </Box>
        ))}
      </Box>
      <Text as={"span"} isTruncated>
        {count} {pluralize("segment", count)}
      </Text>
    </HStack>
  );
}

export function GroupSummary({
  count,
  sampleEntities,
  entityType,
  avatarBackgroundColor = "appBackground",
}: {
  count: number;
  sampleEntities: Entity[];
  entityType: "user" | "company";
  avatarBackgroundColor?: string;
}) {
  const borderRadiusInner = entityType === "company" ? 5 : "full";
  const borderRadiusOuter = entityType === "company" ? 7 : "full";
  return (
    <HStack spacing={0.5}>
      <HStack isolation="isolate" spacing={-1.5}>
        {sampleEntities.map((avatar, i) => {
          const displayText = avatar.name || avatar.id;
          return (
            <Box
              key={avatar.id}
              backgroundColor={avatarBackgroundColor}
              borderRadius={borderRadiusOuter}
              padding={0.5}
              position="relative"
              zIndex={sampleEntities.length - i}
            >
              <Box
                boxSize={5}
                style={{
                  clipPath: "border-box",
                }}
              >
                <Avatar
                  borderRadius={borderRadiusInner}
                  name={!avatar.isUnknown ? displayText : undefined}
                  src={avatar.avatarUrl ?? undefined}
                  type={entityType}
                />
              </Box>
            </Box>
          );
        })}
      </HStack>
      <Text as="span" isTruncated>
        {count} {pluralize(entityType, count)}
      </Text>
    </HStack>
  );
}
